/**
 * Utility functions for detecting and managing environment-specific behavior
 */

/**
 * Detects the current deployment environment based on hostname
 * @returns {'production' | 'beta' | 'dev' | 'local'}
 */
export const getEnvironment = () => {
  const hostname = window.location.hostname;

  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local';
  }

  // Dev environment - Vercel preview URLs for dev branch
  if (hostname.includes('-dev-') || hostname.startsWith('dev.') || hostname.startsWith('dev-')) {
    return 'dev';
  }

  // Beta environment - Vercel preview URLs for beta branch or beta subdomain
  if (hostname.includes('-beta-') || hostname.startsWith('beta.') || hostname.startsWith('beta-')) {
    return 'beta';
  }

  // Production - main domain
  if (hostname === 'theorbitalledger.com' || hostname === 'www.theorbitalledger.com' || hostname === 'theorbitalledger.vercel.app') {
    return 'production';
  }

  // Default to production for unknown domains (safety first)
  return 'production';
};

/**
 * Checks if the current environment requires authentication
 * @returns {boolean}
 */
export const requiresAuthentication = () => {
  const env = getEnvironment();
  return env === 'dev' || env === 'beta';
};

/**
 * Gets the required access level for the current environment
 * @returns {'dev_access' | 'beta_access' | null}
 */
export const getRequiredAccessLevel = () => {
  const env = getEnvironment();

  if (env === 'dev') {
    return 'dev_access';
  }

  if (env === 'beta') {
    return 'beta_access';
  }

  return null;
};

/**
 * Checks if a user has access to the current environment
 * @param {Object} user - User object from AuthContext
 * @returns {boolean}
 */
export const hasEnvironmentAccess = (user) => {
  const env = getEnvironment();

  // Production and local environments are always accessible
  if (env === 'production' || env === 'local') {
    return true;
  }

  // No user means no access to protected environments
  if (!user) {
    return false;
  }

  // Owner and admin roles always have access
  if (user.role === 'owner' || user.role === 'admin') {
    return true;
  }

  // Check environment-specific access flags
  if (env === 'dev') {
    return user.dev_access === true;
  }

  if (env === 'beta') {
    return user.beta_access === true;
  }

  return false;
};

/**
 * Gets a human-readable environment name
 * @returns {string}
 */
export const getEnvironmentName = () => {
  const env = getEnvironment();

  const names = {
    production: 'Production',
    beta: 'Beta Testing',
    dev: 'Development',
    local: 'Local Development'
  };

  return names[env] || 'Unknown';
};

/**
 * Gets an environment-specific message for access denial
 * @returns {string}
 */
export const getAccessDeniedMessage = () => {
  const env = getEnvironment();

  if (env === 'dev') {
    return 'This is a development environment. You need development access to view this site. Please contact an administrator if you believe you should have access.';
  }

  if (env === 'beta') {
    return 'This is a beta testing environment. You need beta access to view this site. Please contact an administrator if you believe you should have access.';
  }

  return 'Access denied to this environment.';
};
